import { motion } from "framer-motion";

export default function MeetNick() {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="relative py-28 md:py-40 scratched-black grain overflow-hidden"
        >
            <div className="absolute top-0 left-0 right-0 h-px hairline-gold" />

            <div className="mx-auto max-w-7xl px-6 md:px-10 relative">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Left rail / label */}
                    <div className="lg:col-span-3">
                        <div className="lg:sticky lg:top-32">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="h-px w-8 bg-gold" />
                                <span className="text-[10px] uppercase tracking-[0.4em] text-gold font-semibold">
                                    01 / Meet Nick
                                </span>
                            </div>
                            <div className="font-display text-cream/90 text-3xl md:text-4xl leading-tight">
                                On-the-ground expert.
                                <br />
                                Campaign manager.
                                <br />
                                <span className="italic text-gold">Campaign strategist.</span>
                            </div>
                            <div className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cream/45">
                                <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                                Est. January 2026
                            </div>
                        </div>
                    </div>

                    {/* Right narrative */}
                    <div className="lg:col-span-9">
                        <motion.blockquote
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7 }}
                            data-testid="about-pullquote"
                            className="font-display italic text-3xl md:text-5xl lg:text-6xl leading-[1.1] text-cream"
                        >
                            "Politics isn't won from behind a desk. It's won{" "}
                            <span className="text-gold not-italic">at the doors</span>,
                            on the phones, at community events, and through building real
                            relationships with voters."
                        </motion.blockquote>

                        <div className="mt-14 space-y-8 max-w-3xl">
                            <motion.p
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="text-cream/75 text-base md:text-lg leading-relaxed"
                            >
                                I'm{" "}
                                <span className="text-cream font-semibold">
                                    Nick Kraus
                                </span>
                                , an <span className="text-gold">Ohio</span>{" "}
                                political consultant, campaign strategist, and
                                field operative. Nicholas Kraus Consulting was
                                founded in{" "}
                                <span className="text-gold">January 2026</span>{" "}
                                after years of working on campaigns at every level
                                throughout Ohio and the{" "}
                                <span className="text-gold">Midwest</span>.
                            </motion.p>

                            <motion.p
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="text-cream/75 text-base md:text-lg leading-relaxed"
                            >
                                From central committee races to statewide
                                campaigns, I have helped candidates build winning
                                operations through disciplined field work, voter
                                contact, volunteer recruitment, data-driven
                                targeting, and grassroots organization. My
                                experience includes managing campaigns, directing
                                field operations, coordinating communications,
                                and solving the day-to-day challenges that
                                determine whether a campaign succeeds or falls
                                short.
                            </motion.p>

                            <motion.p
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="text-cream/75 text-base md:text-lg leading-relaxed"
                            >
                                Thousands of hours in the field and tens of
                                thousands of voter conversations have taught me a
                                simple lesson:{" "}
                                <span className="text-cream font-semibold">
                                    elections are won by campaigns that outwork
                                    their opponents.
                                </span>{" "}
                                My job is to help candidates build that advantage
                                and carry it through Election Day.
                            </motion.p>
                        </div>

                        {/* Output strip */}
                        <div
                            data-testid="about-cred-strip"
                            className="mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-b border-gold/20"
                        >
                            {[
                                { v: "Central\nCommittee", k: "Party Building" },
                                { v: "School\nBoard", k: "Local Races" },
                                { v: "County Commissioner", k: "County" },
                                { v: "Legislative · Statewide", k: "State Office" },
                            ].map((c, i) => (
                                <div
                                    key={i}
                                    className={`px-4 py-5 ${
                                        i !== 3 ? "md:border-r" : ""
                                    } border-gold/15 ${i < 2 ? "border-b md:border-b-0" : ""} ${
                                        i % 2 === 0 ? "border-r md:border-r" : ""
                                    }`}
                                >
                                    <div className="font-display text-xl md:text-2xl text-cream whitespace-pre-line leading-tight">
                                        {c.v}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-gold/80 mt-1">
                                        {c.k}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
